import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGeo } from '@/context/GeoContext';

const GA_MEASUREMENT_ID = 'G-BD9WQCNXKH';

/**
 * GeoAwareAnalytics - Only initializes Google Analytics after geo-check confirms
 * the user is from an allowed country. This prevents tracking visitors from blocked countries.
 */
export const GeoAwareAnalytics = () => {
  const location = useLocation();
  const { geoState, isAnalyticsAllowed } = useGeo();
  const gaInitialized = useRef(false);
  const lastTrackedPath = useRef<string | null>(null);

  // Initialize GA only after geo-check confirms allowed country
  useEffect(() => {
    if (!geoState.isChecked) {
      console.log('[GeoAwareAnalytics] Waiting for geo-check...');
      return;
    }

    if (!isAnalyticsAllowed()) {
      console.log('[GeoAwareAnalytics] Analytics blocked for country:', geoState.countryCode);
      // Mark GA as blocked to prevent any tracking
      (window as any).__GA_BLOCKED = true;
      return;
    }

    if (gaInitialized.current) {
      return;
    }

    // Ensure the gtag.js script has been loaded by index.html
    if (!(window as any).__GA_LOADED) {
      console.log('[GeoAwareAnalytics] gtag.js not yet loaded, waiting...');
      return;
    }

    // Initialize GA now that we know user is from allowed country
    console.log('[GeoAwareAnalytics] Initializing GA for allowed country:', geoState.countryCode);
    
    // dataLayer and gtag are already initialized by index.html -- do not re-create them
    if (typeof (window as any).gtag !== 'function') {
      console.warn('[GeoAwareAnalytics] gtag function not found despite __GA_LOADED');
      return;
    }

    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      page_path: location.pathname,
      send_page_view: true,
      cookie_flags: 'SameSite=None;Secure',
      custom_map: {
        dimension1: 'country_code',
      },
      country_code: geoState.countryCode,
    });

    gaInitialized.current = true;
    lastTrackedPath.current = location.pathname;
    
    console.log('[GeoAwareAnalytics] GA initialized, first pageview tracked:', location.pathname);
  }, [geoState.isChecked, geoState.countryCode, isAnalyticsAllowed, location.pathname]);

  // Track subsequent page views (only if GA was initialized)
  useEffect(() => {
    if (!gaInitialized.current) return;
    if (!isAnalyticsAllowed()) return;
    if (lastTrackedPath.current === location.pathname) return;
    
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname,
      });
      lastTrackedPath.current = location.pathname;
      console.log('[GeoAwareAnalytics] Pageview tracked:', location.pathname);
    }
  }, [location.pathname, isAnalyticsAllowed]);

  return null;
};
